import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";

import CalendarIcon from "./CalendarIcon";
import { dateRangeVariable, useDateRange } from "./modelsDatePickers";
import StyledDatePickerWrapper from "./StyledDatePicker";

import "react-datepicker/dist/react-datepicker.css";

export type Dates = [Date | null, Date | null];

type Props = {
  placeholderText?: string;
  dateFormat?: string;
  mode?: "range" | "single"; // range: start and end date, single: single date
  onChange?: (value: Dates | Date | null) => void;
  value?: Dates | Date | null; // value: start and end date or single date
  error?: boolean;
  isClearable?: boolean;
  onChangeRaw?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filterDate?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  renderDayContents?: (day: number, date?: Date) => React.ReactNode;
  calendarClassName?: string;
};

const ReactDatePickerContainer: FC<Props> = ({
  placeholderText,
  dateFormat,
  mode = "range",
  onChange,
  value,
  isClearable = true,
  error,
  onChangeRaw,
  filterDate,
  minDate,
  maxDate,
  renderDayContents,
  calendarClassName,
}): JSX.Element => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const isControlled = useMemo(
    () => typeof value !== "undefined" && typeof onChange === "function",
    [value, onChange]
  );
  const shouldSyncGlobal = useMemo(() => !isControlled, [isControlled]);

  const handleDateChange = useCallback(
    (value: Dates | Date | null): void => {
      if (mode === "range") {
        const [start, end] = (value as Dates) ?? [null, null];

        if (!isControlled) {
          setStartDate(start);

          setEndDate(end);
        }

        if (shouldSyncGlobal && start && end) {
          const endOfDay = end;

          endOfDay.setHours(23);

          endOfDay.setMinutes(59);

          endOfDay.setSeconds(59);

          dateRangeVariable({
            from: start,
            to: new Date(endOfDay),
          });
        }

        if (shouldSyncGlobal && !start && !end) {
          dateRangeVariable({
            from: null,
            to: null,
          });
        }

        if (isControlled && onChange) onChange([start, end]);

        return;
      }

      const single = value as Date | null;

      if (!isControlled) {
        setStartDate(single);

        setEndDate(null);
      }

      if (shouldSyncGlobal) {
        dateRangeVariable({
          from: single,
          to: null,
        });
      }

      if (isControlled && onChange) onChange(single);
    },
    [mode, shouldSyncGlobal, isControlled, onChange]
  );

  const dateRange = useDateRange();

  useEffect((): void => {
    if (!shouldSyncGlobal) return;

    if (dateRange) {
      setStartDate(dateRange.from);

      setEndDate(dateRange.to);
    } else {
      setStartDate(null);

      setEndDate(null);
    }
  }, [dateRange, shouldSyncGlobal]);

  useEffect((): (() => void) => {
    return (): void => {
      setStartDate(null);

      setEndDate(null);

      if (shouldSyncGlobal) {
        dateRangeVariable(null);
      }
    };
  }, [shouldSyncGlobal]);

  const computedStartDate = useMemo<Date | null>(() => {
    if (!isControlled) return startDate;

    if (mode === "single") return (value as Date | null) ?? null;

    const [s] = ((value as Dates) ?? [null, null]) as Dates;

    return s ?? null;
  }, [isControlled, mode, value, startDate]);

  const computedEndDate = useMemo<Date | null>(() => {
    if (!isControlled) return endDate;

    if (mode === "single") return null;

    const [, e] = ((value as Dates) ?? [null, null]) as Dates;

    return e ?? null;
  }, [isControlled, mode, value, endDate]);

  return (
    <StyledDatePickerWrapper error={error}>
      <DatePicker
        selected={computedStartDate}
        onChange={handleDateChange}
        icon={<CalendarIcon />}
        showIcon
        startDate={mode === "range" ? computedStartDate : undefined}
        endDate={mode === "range" ? computedEndDate : undefined}
        selectsRange={mode === "range"}
        maxDate={maxDate ?? new Date()}
        minDate={minDate}
        monthsShown={mode === "range" ? 2 : 1}
        formatWeekDay={(nameOfDay: string): string => nameOfDay.slice(0, 3)}
        placeholderText={placeholderText}
        dateFormat={dateFormat}
        isClearable={isClearable}
        onChangeRaw={onChangeRaw}
        filterDate={filterDate}
        renderDayContents={renderDayContents}
        calendarClassName={calendarClassName}
        popperContainer={({ children }) =>
          typeof document !== "undefined"
            ? createPortal(children, document.body)
            : children
        }
      />
    </StyledDatePickerWrapper>
  );
};

const ReactDatePicker = memo(ReactDatePickerContainer);

export default ReactDatePicker;
